import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../context/PermissionsContext';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

import { patchesAPI } from '../services/api';

function PatchList() {
  const { canApprovePatches, isAdmin } = usePermissions();
  const canChange = isAdmin();
  const navigate = useNavigate();
  const [patches, setPatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [rejectDialog, setRejectDialog] = useState({ open: false, patchId: null });
  const [rejectReason, setRejectReason] = useState('');

  // Default to current month/year
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());

  useEffect(() => {
    loadPatches();
  }, [selectedMonth, selectedYear]);

  const loadPatches = async () => {
    try {
      setLoading(true);
      const response = await patchesAPI.list(null, selectedMonth, selectedYear);
      setPatches(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to load patches');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this patch?')) return;

    try {
      await patchesAPI.delete(id);
      setSuccess('Patch deleted');
      loadPatches();
    } catch (err) {
      setError('Failed to delete patch');
    }
  };

  const handleSendForApproval = async () => {
    const draftCount = patches.filter(p => p.status === 'draft').length;
    if (!window.confirm(`Send ${draftCount} draft patches for approval?`)) return;

    try {
      const response = await patchesAPI.sendForApproval();
      setSuccess(`${response.data.patch_count} patches sent for approval`);
      loadPatches();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send for approval');
    }
  };

  const handlePreviewPDF = async () => {
    try {
      const response = await patchesAPI.previewPDF();
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate PDF preview');
    }
  };

  const handleApproveAll = async () => {
    const pendingCount = patches.filter(p => p.status === 'pending_approval').length;
    if (!window.confirm(`Approve all ${pendingCount} patches and send to customers?`)) return;

    try {
      const response = await patchesAPI.approveAll();
      setSuccess(`${response.data.patch_count} patches approved and PDF generated!`);
      loadPatches();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to approve patches');
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      setError('Please provide a reason for rejection');
      return;
    }

    try {
      await patchesAPI.reject(rejectDialog.patchId, rejectReason);
      setSuccess('Patch rejected and team notified');
      setRejectDialog({ open: false, patchId: null });
      setRejectReason('');
      loadPatches();
    } catch (err) {
      setError('Failed to reject patch');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft':
        return 'default';
      case 'pending_approval':
        return 'warning';
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const getClassificationColor = (classification) => {
    switch (classification) {
      case 'Critical':
        return 'error';
      case 'Important':
        return 'warning';
      case 'Moderate':
        return 'info';
      case 'Low':
        return 'success';
      default:
        return 'default';
    }
  };

  const draftCount = patches.filter(p => p.status === 'draft').length;
  const pendingCount = patches.filter(p => p.status === 'pending_approval').length;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];

  const years = [];
  const currentYear = new Date().getFullYear();
  for (let i = currentYear; i >= currentYear - 5; i--) {
    years.push(i);
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
        <Typography variant="h4">Patches</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {canChange && draftCount > 0 && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<SendIcon />}
              onClick={handleSendForApproval}
            >
              Send for Approval ({draftCount})
            </Button>
          )}
          {pendingCount > 0 && (
            <>
              {canChange && (
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<PictureAsPdfIcon />}
                  onClick={handlePreviewPDF}
                >
                  Preview PDF
                </Button>
              )}
              {canApprovePatches() && (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<CheckCircleIcon />}
                  onClick={handleApproveAll}
                >
                  Approve All ({pendingCount})
                </Button>
              )}
            </>
          )}
          {canChange && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/patches/new')}
            >
              Add Patch
            </Button>
          )}
        </Box>
      </Box>

      {/* Month/Year Filter */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Typography variant="subtitle1">Filter by:</Typography>
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Month</InputLabel>
            <Select
              value={selectedMonth}
              label="Month"
              onChange={(e) => setSelectedMonth(e.target.value)}
            >
              {months.map((month) => (
                <MenuItem key={month.value} value={month.value}>
                  {month.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel>Year</InputLabel>
            <Select
              value={selectedYear}
              label="Year"
              onChange={(e) => setSelectedYear(e.target.value)}
            >
              {years.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>KB Number</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Classification</TableCell>
              <TableCell>Month Applied</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {patches.map((patch) => (
              <TableRow key={patch.id}>
                <TableCell>
                  <strong>{patch.kb_number}</strong>
                </TableCell>
                <TableCell sx={{ maxWidth: 300 }}>
                  {patch.description}
                </TableCell>
                <TableCell>
                  <Chip
                    label={patch.classification}
                    color={getClassificationColor(patch.classification)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {patch.date_applied ? new Date(patch.date_applied).toLocaleDateString('en-US', { year: 'numeric', month: 'long' }) : '-'}
                </TableCell>
                <TableCell>
                  <Chip
                    label={patch.status.replace('_', ' ')}
                    color={getStatusColor(patch.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {canChange && patch.status === 'draft' && (
                    <>
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/patches/${patch.id}`)}
                        title="Edit"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(patch.id)}
                        title="Delete"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </>
                  )}
                  {patch.status === 'pending_approval' && canApprovePatches() && (
                    <>
                      <IconButton
                        size="small"
                        color="success"
                        onClick={() => patchesAPI.approve(patch.id).then(loadPatches)}
                        title="Approve"
                      >
                        <CheckCircleIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setRejectDialog({ open: true, patchId: patch.id })}
                        title="Reject"
                      >
                        <CancelIcon />
                      </IconButton>
                    </>
                  )}
                  {patch.status === 'rejected' && (
                    <Typography variant="caption" color="error">
                      {patch.rejection_reason}
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {patches.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No patches found. Add your first patch!
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Reject Dialog */}
      <Dialog open={rejectDialog.open} onClose={() => setRejectDialog({ open: false, patchId: null })}>
        <DialogTitle>Reject Patch</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Reason for Rejection"
            fullWidth
            multiline
            rows={4}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Explain why this patch is being rejected..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog({ open: false, patchId: null })}>
            Cancel
          </Button>
          <Button onClick={handleReject} variant="contained" color="error">
            Reject & Notify Team
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default PatchList;
